import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import ProjectTemplate from '../ProjectTemplate';
import SummaryBox from '../SummaryBox';
import './Gotyou.css';

const MobilePhoneIcon = () => (
  <svg
    className="hero-cta-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const Gotyou = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  return (
    <div className="project-page gotyou">
      <div className="container">
        <div className="back-button">
          <button onClick={handleBackToHome} className="back-link">
            ← Back to Home
          </button>
        </div>

        <div className="project-hero">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="project-title">
                Design to code:
                <br />
                GOTYOU app
              </h1>
              <p className="project-subtitle">
                Architecting a mobile consumer experience from initial ideation to an interactive, living implementation.
              </p>
              <a
                href="https://gotyou-ten.vercel.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-cta-button"
              >
                <MobilePhoneIcon />
                Launch Live Demo
              </a>
            </div>
            <div className="hero-image">
              <img
                src="/GOTYOU_Drop.gif"
                alt="GotYou mobile app"
                onError={(e) => {
                  e.target.src = '/placeholder-large.jpg';
                }}
              />
            </div>
          </div>
        </div>

        <ProjectTemplate
          customSectionBeforeSummary={
            <>
              <div className="goal-section">
                <h3 className="section-title">Goal</h3>
                <div className="goal-content">
                  <div className="goal-text">
                    Transforming routine local commerce into an exciting, mobile-first daily ritual. By executing the full product lifecycle from concept to code, my goal was to design and engineer an application centered around high-energy, synchronized interaction states that drive genuine daily engagement.
                  </div>
                  <div className="tldr-image-container">
                    <img
                      src="/projects/TLDR-gotyou.png"
                      alt="GotYou TL;DR"
                      className="tldr-image"
                      onError={(e) => {
                        e.target.src = '/placeholder-large.jpg';
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="live-implementation-section">
                <h3 className="section-title">Design to Code</h3>
                <div className="live-implementation-content section-content">
                  <p>
                    Rather than stopping at static Figma handoffs, I built a fully functional, mobile-first live version of the application to test layout responsiveness, system logic, and state transitions in production.
                  </p>
                  <a
                    href="https://gotyou-ten.vercel.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hero-cta-button"
                  >
                    <MobilePhoneIcon />
                    Launch Live Demo
                  </a>
                </div>
              </div>

              <div className="summary-section hero-entrance-5">
                <h3 className="section-title">At a Glance</h3>
                <div className="summary-grid section-content">
                  <SummaryBox
                    icon="/icons/role.png"
                    title="My Role"
                    content="<strong>End-to-End Product Design</strong><br/><br/>UX/UI architecture, branding, motion design, and frontend development."
                    className="hero-entrance-6"
                  />
                  <SummaryBox
                    icon="/icons/tools.png"
                    title="Tools"
                    content="<strong>Design &amp; Code</strong><br/><br/>Figma, Ideogram AI, Cursor, Claude Code, Antigravity Framework, and responsive HTML/CSS."
                    className="hero-entrance-7"
                  />
                  <SummaryBox
                    icon="/icons/collaboration.png"
                    title="Core Focus"
                    content="<strong>AI based Design, Mobile-First</strong><br/><br/>Real-time interaction states, glassmorphic UI systems, and gamified loyalty loops."
                    className="hero-entrance-8"
                  />
                  <SummaryBox
                    icon="/icons/timeline.png"
                    title="Timeline"
                    content="<strong>4 Months</strong><br/><br/>From initial line-art concepts and user flows to a functional, deployed deployment."
                    className="hero-entrance-9"
                  />
                </div>
              </div>
            </>
          }
          hideSummarySection
        />

        <div className="challenges-section">
          <h3 className="challenges-title">Challenges and solutions</h3>
        </div>

        <div className="challenge-section">
          <h3 className="challenge-title">
            1st Challenge:<br />
            <strong>Designing a Daily Ritual: The Psychology of the Drop</strong>
          </h3>
          <div className="challenge-intro-text section-content">
            <p>
              To build a genuine daily habit and combat app fatigue, I moved away from passive push notifications and designed the &quot;Daily Deal Drop&quot; around the psychology of anticipation. I architected distinct user states: the &apos;before&apos; build-up, the high-energy &apos;hype&apos; window, and the &apos;closure&apos; aftermath. To mentally separate the active drop from the rest of the application, I utilized a distinguished dark mode specifically for the live state. This stark visual contrast creates a clear mental model for the user—they are stepping into an exclusive, time-boxed event, and stepping back out once the drop is claimed.
            </p>
          </div>

          <div className="challenge-image-groups">
            <div className="challenge-image-group">
              <h4 className="challenge-image-group-title">The wait and the hype - Building the excitement</h4>
              <div className="challenge-images-grid">
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Before.png"
                    alt="GotYou wait state - building anticipation"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Active.png"
                    alt="GotYou hype window - deals dropping now"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="challenge-image-group">
              <h4 className="challenge-image-group-title">Dark mode for exclusive time-boxed event. Swipe to like deals</h4>
              <div className="challenge-images-grid">
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Active_Swipe.png"
                    alt="GotYou dark mode live drop - swipe to like"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Catch.png"
                    alt="GotYou deal claimed - nice catch"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="challenge-section">
          <h3 className="challenge-title">
            2nd Challenge:<br />
            <strong>Frictionless Discovery: Map Smart Sorting &amp; Tik-Tok-Style Exploration</strong>
          </h3>
          <div className="challenge-intro-text section-content">
            <p>
              Local commerce apps often fail by overwhelming users with dense, static lists. To solve this, I implemented a &quot;Hot vs. Near&quot; smart slider, giving users immediate control over their discovery parameters based on either real-time popularity or geographic proximity. Because the target persona leans heavily Gen Z, I bypassed traditional grid layouts in favor of a TikTok-style vertical feed for business &quot;reels.&quot; This leverages an established, highly engaging mental model, allowing users to rapidly evaluate the vibe of a local spot through short-form video before committing to a visit.
            </p>
          </div>

          <div className="challenge-image-groups">
            <div className="challenge-image-group">
              <h4 className="challenge-image-group-title">Hot vs. Near — smart sorting on the map</h4>
              <div className="challenge-images-grid challenge-images-grid-single">
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Map.png"
                    alt="GotYou explore map with deal pins and filters"
                    className="challenge-image challenge-image-same-size challenge-image-no-border"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="challenge-image-group">
              <h4 className="challenge-image-group-title">TikTok-style vertical feed for business reels</h4>
              <div className="challenge-images-grid challenge-images-grid-single">
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_FOTYOU.png"
                    alt="GotYou For You vertical video feed"
                    className="challenge-image challenge-image-same-size"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="challenge-section">
          <h3 className="challenge-title">
            3rd Challenge:<br />
            <strong>Sustained Engagement: Streaks and Meaningful Gamification</strong>
          </h3>
          <div className="challenge-intro-text section-content">
            <p>
              Gamification only works if it drives real-world behavior, rather than just superficial screen time. I designed the leveling system to forge actual, recurring connections between the user and local brick-and-mortar businesses. Instead of relying on generic badges, the gamification is seamlessly integrated into the core platform experience. By incorporating psychological triggers like daily &apos;streaks&apos; for check-ins and redemptions, the system rewards habitual loyalty and transforms the act of supporting local commerce from a transactional errand into a deeply rewarding progression loop.
            </p>
          </div>

          <div className="challenge-image-group challenge-image-group-wide">
            <h4 className="challenge-image-group-title">Avatar-based leveling system tied to loyalty behavior</h4>
            <div className="challenge-images-grid challenge-images-grid-single">
              <div className="challenge-image-item challenge-image-item-landscape">
                <img
                  src="/projects/GOTYOU_Leveling_System.png"
                  alt="GotYou leveling system avatars and progression tiers"
                  className="challenge-image challenge-image-landscape"
                  onError={(e) => {
                    e.target.src = '/placeholder-large.jpg';
                  }}
                />
              </div>
            </div>
          </div>

          <div className="challenge-image-groups">
            <div className="challenge-image-group">
              <h4 className="challenge-image-group-title">Local status and streak mechanics</h4>
              <div className="challenge-images-grid">
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_LocalStatus.png"
                    alt="GotYou local status card with streak progress"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_LocalStatus2.png"
                    alt="GotYou community and local status leaderboard"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="challenge-image-group">
              <h4 className="challenge-image-group-title">Reward loop moments in real user flow</h4>
              <div className="challenge-images-grid">
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Game.png"
                    alt="GotYou favorite spot unlocked reward moment"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
                <div className="challenge-image-item">
                  <img
                    src="/projects/GOTYOU_Game2.png"
                    alt="GotYou deal rewards and generated value card"
                    className="challenge-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-large.jpg';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gotyou;
